<template>
  <div>
    <nav
      class="fixed top-0 left-0 right-0 z-50 h-[66px] px-10 flex items-center justify-between backdrop-blur-xl border-b transition-colors duration-300"
      :class="
        isLight
          ? 'bg-[#e4f4f0]/90 border-[#d0ede8]/50'
          : 'bg-[#0f1f1c]/90 border-white/[0.06]'
      "
    >
      <!-- Logo -->
      <a
        href="/"
        class="font-display text-xl font-extrabold tracking-tight no-underline"
        :class="isLight ? 'text-[#0f1f1c]' : 'text-white'"
      >
        trekko<span :class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'">.</span>
      </a>

      <!-- Nav links -->
      <div class="flex items-center gap-2">

        <!-- Language switcher — desktop only (hidden on mobile) -->
        <div class="relative mr-1 hidden sm:block" ref="langDropdownDesktop">
          <button
            @click="desktopOpen = !desktopOpen"
            class="w-8 h-8 flex items-center justify-center rounded-full border transition-all duration-200"
            :class="
              desktopOpen
                ? isLight
                  ? 'bg-white border-[#1a9b8a]/40 text-[#0d6b5e]'
                  : 'bg-white/10 border-[#1a9b8a]/40 text-white'
                : isLight
                ? 'border-[#d0ede8] text-[#6b8f89] hover:border-[#1a9b8a]/40 hover:text-[#0d6b5e]'
                : 'border-white/10 text-[#a8d5cc] hover:border-[#1a9b8a]/40 hover:text-white'
            "
            :aria-label="$t('nav.language')"
          >
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.75">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253M3 12c0 .778.099 1.533.284 2.253" />
            </svg>
          </button>

          <transition name="lang-drop">
            <div
              v-if="desktopOpen"
              class="absolute right-0 top-full mt-2 rounded-xl border shadow-xl overflow-hidden min-w-[148px]"
              :class="
                isLight
                  ? 'bg-white border-[#1a9b8a]/15 shadow-black/10'
                  : 'bg-[#132420] border-white/[0.08] shadow-black/40'
              "
            >
              <button
                v-for="lang in languages"
                :key="lang.code"
                @click="setLocale(lang.code)"
                class="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-left transition-colors duration-150"
                :class="
                  $i18n.locale === lang.code
                    ? isLight
                      ? 'bg-[#1a9b8a]/10 text-[#0d6b5e] font-semibold'
                      : 'bg-[#1a9b8a]/15 text-[#a8d5cc] font-semibold'
                    : isLight
                    ? 'text-[#0f1f1c]/70 hover:bg-[#1a9b8a]/8 hover:text-[#0d6b5e]'
                    : 'text-white/60 hover:bg-white/[0.05] hover:text-white'
                "
              >
                <span class="text-base leading-none">{{ lang.flag }}</span>
                <span class="flex-1">{{ lang.label }}</span>
                <svg
                  v-if="$i18n.locale === lang.code"
                  class="w-3 h-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                </svg>
              </button>
            </div>
          </transition>
        </div>

        <a
          href="/discover"
          class="text-sm font-medium px-4 py-2 rounded-full transition-all duration-200 no-underline"
          :class="
            isLight
              ? 'text-[#0d6b5e] hover:text-[#0f1f1c] hover:bg-[#1a9b8a]/10'
              : 'text-[#a8d5cc] hover:text-white hover:bg-white/7'
          "
        >
          {{ $t('nav.discover') }}
        </a>
        <a
          :href="dashboardUrl"
          target="_blank"
          class="text-sm font-medium px-5 py-2 rounded-full no-underline transition-all duration-200 text-white hover:-translate-y-px"
          :class="
            isLight
              ? 'bg-[#1a9b8a] hover:bg-[#0d6b5e]'
              : 'bg-[#1a9b8a] hover:bg-[#0d6b5e]'
          "
        >
          {{ $t('nav.hostEvent') }}
        </a>
      </div>
    </nav>

    <!-- Language switcher — mobile only, fixed bottom-left -->
    <div class="fixed bottom-4 left-4 z-[9999] sm:hidden" ref="langDropdownMobile">
      <transition name="lang-drop-up">
        <div
          v-if="mobileOpen"
          class="absolute bottom-full left-0 mb-2 rounded-xl border shadow-xl overflow-hidden min-w-[148px]"
          :class="
            isLight
              ? 'bg-white border-[#1a9b8a]/15 shadow-black/10'
              : 'bg-[#132420] border-white/[0.08] shadow-black/40'
          "
        >
          <button
            v-for="lang in languages"
            :key="lang.code"
            @click="setLocale(lang.code)"
            class="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-left transition-colors duration-150"
            :class="
              $i18n.locale === lang.code
                ? isLight
                  ? 'bg-[#1a9b8a]/10 text-[#0d6b5e] font-semibold'
                  : 'bg-[#1a9b8a]/15 text-[#a8d5cc] font-semibold'
                : isLight
                ? 'text-[#0f1f1c]/70 hover:bg-[#1a9b8a]/8 hover:text-[#0d6b5e]'
                : 'text-white/60 hover:bg-white/[0.05] hover:text-white'
            "
          >
            <span class="text-base leading-none">{{ lang.flag }}</span>
            <span class="flex-1">{{ lang.label }}</span>
            <svg
              v-if="$i18n.locale === lang.code"
              class="w-3 h-3 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>
      </transition>

      <button
        @click="mobileOpen = !mobileOpen"
        class="w-10 h-10 flex items-center justify-center rounded-full border shadow-lg transition-all duration-200"
        :class="
          mobileOpen
            ? isLight
              ? 'bg-white border-[#1a9b8a]/50 text-[#0d6b5e] shadow-[#1a9b8a]/15'
              : 'bg-[#132420] border-[#1a9b8a]/50 text-[#a8d5cc] shadow-black/40'
            : isLight
            ? 'bg-[#e4f4f0] border-[#d0ede8] text-[#6b8f89] shadow-black/10 hover:border-[#1a9b8a]/40 hover:text-[#0d6b5e]'
            : 'bg-[#0f1f1c] border-white/10 text-[#a8d5cc] shadow-black/40 hover:border-[#1a9b8a]/40 hover:text-white'
        "
        :aria-label="$t('nav.language')"
      >
        <svg class="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.75">
          <path stroke-linecap="round" stroke-linejoin="round"
            d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253M3 12c0 .778.099 1.533.284 2.253" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "NavBarComponent",
  props: { isLight: Boolean },
  data() {
    return {
      dashboardUrl: process.env.VUE_APP_DASHBOARD_URL || "#",
      desktopOpen: false,
      mobileOpen: false,
      languages: [
        { code: "en", label: "English", flag: "🇬🇧" },
        { code: "it", label: "Italiano", flag: "🇮🇹" },
      ],
    };
  },
  mounted() {
    document.addEventListener("click", this.onClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.onClickOutside);
  },
  methods: {
    setLocale(code) {
      this.$i18n.locale = code;
      localStorage.setItem("trekko_lang", code);
      this.desktopOpen = false;
      this.mobileOpen = false;
    },
    onClickOutside(e) {
      if (this.$refs.langDropdownDesktop && !this.$refs.langDropdownDesktop.contains(e.target)) {
        this.desktopOpen = false;
      }
      if (this.$refs.langDropdownMobile && !this.$refs.langDropdownMobile.contains(e.target)) {
        this.mobileOpen = false;
      }
    },
  },
};
</script>

<style scoped>
.lang-drop-enter-active,
.lang-drop-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.lang-drop-enter-from,
.lang-drop-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.lang-drop-up-enter-active,
.lang-drop-up-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.lang-drop-up-enter-from,
.lang-drop-up-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>